import { FC, ReactElement, ReactNode, useCallback, useMemo, useState } from 'react';
import _ from 'lodash';
import Style from './useFormOverview.module.scss';
import { useModal } from '../../../application/hooks/useModal';
import {
  FormLifecycleState,
  IFormResultVersionDto,
  IRouteProgressDto,
  MonitoringReviewStatusEnum,
  RecallFormResultApprovalDto,
  StudyRoleClaimValue,
} from '../../../services/api/api-client';
import { AppButton, AppButtonProps } from 'uikit/buttons';
import { FormModalWithContext } from 'src/components/formEditor/modals/formModalWithContext';
import { IssueMark } from 'src/components/issue/issueTarget/issueMark.component';
import { EditingResult, StartEditingArgs } from '../editing/useFormEditing';
import {
  getFormHistoryByIdQueryKey,
  getFormResultByIdQueryKey,
  getFormResultsQueryKey,
  useGetFormByIdQuery,
  useGetFormResultByIdQuery,
  useGetFormResultsQuery,
} from 'src/services/api/api-client/FormsQuery';
import {
  approveFormResult,
  markFormResultAsDraft,
  recallFormResultApproval,
} from 'src/services/api/api-client/FormsClient';
import { useQueryClient } from '@tanstack/react-query';
import { FormScoreComponent } from './FormScore.component';
import { OverviewHeader } from './headers/OverviewHeader';
import { useRecallApprovalReason } from './useRecallApprovalReason.component';
import { EcrfOverviewHeader } from './headers/EcrfOverviewHeader';
import {
  getPatientsQueryKey,
  getPatientStudyCountersQueryKey,
  useGetPatientQuery,
} from '../../../services/api/api-client/PatientQuery';
import { useScopedTranslation } from '../../../application/localisation/useScopedTranslation';
import { useFormResultSkipRevoking } from '../../../components/formEditor/modals/formResultSkipRevokingModal/useFormResultSkipRevoking';
import { Trans } from 'react-i18next';
import { ConfirmResult, useConfirmationModal } from '../../../components/dialogModal/useConfirmationModal';
import { conditionalConcat } from '../../../helpers/arrayHelpers';
import { useHasAccessToForm, useHasStudyClaims } from '../../../helpers/auth/auth-helper';
import { Tooltip } from 'uikit/tooltip/tooltip.component';
import { TypographyStyles } from 'styles';
import {
  getPatientRouteProgressQueryKey,
  useGetPatientRouteProgressQuery,
} from 'src/services/api/api-client/StudyRoutesQuery';
import { useFinishingPatientFormState } from './useFinishingPatientFormState';
import { OverviewNavbar, OverviewNavbarItemProps } from 'uikit/fields/overview/OverviewNavbar';
import { DateFormats, localFormat } from 'src/helpers/date-helpers';
import { useStudy } from 'src/helpers/hooks/useStudy';
import { showServerErrorToast } from 'src/components/toast/toast-helper';
import { ReactComponent as HistoryIcon } from '../../../assets/img/common/history_20.svg';
import { ReactComponent as MenuIcon } from '../../../assets/img/common/menu_20.svg';
import { Tabs } from 'uikit/tabs/tabs';

export type OpenOverviewArgs = {
  formResultId: number;
  navigateToNodeId?: string;
};

export enum FormTab {
  FormOverview,
  HistoryOfChanges,
}

export const useFormOverview = (props?: { onEdit: (args: StartEditingArgs) => Promise<EditingResult> }) => {
  const client = useQueryClient();
  const study = useStudy();

  const overviewModal = useModal();
  const recallApproval = useRecallApprovalReason();
  const { hasAccessToForm } = useHasAccessToForm();

  const [resultId, setResultId] = useState<number>();
  const [nodeId, setNodeId] = useState<string | undefined>();
  const [formTab, setFormTab] = useState<FormTab>(FormTab.FormOverview);
  const [isApproveRecalling, setIsApproveRecalling] = useState(false);

  // #region Queries

  const { data: formResult, isFetching: isFormResultInitialLoading } = useGetFormResultByIdQuery(
    { resultId: resultId! },
    { enabled: !!resultId, suspense: false, keepPreviousData: true },
  );

  const { data: formConfig, isFetching: isFormConfigInitialLoading } = useGetFormByIdQuery(
    { id: formResult?.formConfigId || 0 },
    {
      enabled: !!formResult?.formConfigId && hasAccessToForm(formResult?.formConfigId),
      suspense: false,
      keepPreviousData: true,
    },
  );

  const { data: patient } = useGetPatientQuery(formResult?.patientId ?? '', {
    enabled: !!formResult?.patientId,
    keepPreviousData: true,
  });

  const { data: routeProgress } = useGetPatientRouteProgressQuery(
    { patientId: patient?.id ?? '' },
    { enabled: !!patient?.id && !formConfig?.isMultiInstance, suspense: false },
  );

  const { data: multipleResults, isInitialLoading: isMultipleResultsInitialLoading } = useGetFormResultsQuery(
    { studyId: study!.id, patientId: patient?.id ?? '', formConfigId: formConfig?.id, lastFormResultVersionOnly: true },
    { enabled: !!patient?.id && formConfig?.isMultiInstance, suspense: false },
  );

  // #endregion

  const isLoading = useMemo(
    () =>
      isFormResultInitialLoading ||
      isFormConfigInitialLoading ||
      // NOTE: Another forms loading states when implementing multi-instance forms overview
      // isPatientLoading ||
      // isRouteProgressInitialLoading ||
      isMultipleResultsInitialLoading,
    [isFormConfigInitialLoading, isFormResultInitialLoading, isMultipleResultsInitialLoading],
  );

  const currentFormResultVersion = useMemo(() => _.last(formResult?.versions), [formResult?.versions]);

  const currentStep = useMemo(
    () => routeProgress?.steps.find((x) => x.forms.some((f) => f.formResultId === formResult?.id)),
    [formResult?.id, routeProgress?.steps],
  );

  const navbarItems: OverviewNavbarItemProps[] = useMemo(() => {
    const filledSteps =
      routeProgress?.steps
        .map((x) => ({ stepName: x.name, forms: x.forms.filter((f) => f.formConfigId === formConfig?.id) }))
        .filter((x) => x.forms.some((f) => f.formResultId))
        .map<OverviewNavbarItemProps>((step, i, arr) => {
          return {
            key: step.stepName,
            selected: step.stepName === currentStep?.name,
            disabled: arr.length === 1,
            text: step.stepName,
            onClick: () => {
              const nextForm = _.first(step.forms);
              if (!nextForm) return;

              recallApproval.close();
              setResultId(nextForm.formResultId || undefined);
            },
          };
        }) || [];

    const filledMultipleForms =
      multipleResults?.data
        .map((x) => ({ createdAt: x.createdAt, resultId: x.id }))
        .sort((a, b) => a.createdAt.getTime() - b.createdAt.getTime())
        .map((x, i, arr) => {
          return {
            key: x.resultId,
            selected: x.resultId === formResult?.id,
            disabled: arr.length === 1,
            text: localFormat(x.createdAt, DateFormats.longDate),
            onClick: () => {
              recallApproval.close();
              setResultId(x.resultId);
            },
          };
        }) || [];

    return formConfig?.isMultiInstance ? filledMultipleForms : filledSteps;
  }, [
    routeProgress?.steps,
    multipleResults?.data,
    formConfig?.isMultiInstance,
    formConfig?.id,
    currentStep?.name,
    recallApproval,
    formResult?.id,
  ]);

  //#region Editing

  const onEditResult = useCallback(
    async (options?: { asSubmitted?: boolean; recallReason?: string }) => {
      overviewModal.closeModal();

      await props?.onEdit({
        formResult: formResult!,
        form: formConfig,
        asSubmitted: options?.asSubmitted,
        recallReason: options?.recallReason,
        stepName: currentStep?.name,
      });

      overviewModal.openModal();
    },
    [overviewModal, props, formResult, formConfig, currentStep?.name],
  );

  //#endregion

  const openOverview = useCallback(
    async (args: OpenOverviewArgs) => {
      setResultId(args.formResultId);
      setNodeId(args.navigateToNodeId);
      setFormTab(FormTab.FormOverview);

      overviewModal.openModal();
    },
    [overviewModal],
  );

  const closeOverview = useCallback(() => {
    overviewModal.closeModal();

    setResultId(undefined);
    setNodeId(undefined);
  }, [overviewModal]);

  const recallApprovalHandler = useCallback(async () => {
    if (!formResult || !currentFormResultVersion) return;

    const result = await recallApproval.open();
    if (result.state === 'aborted') return;

    try {
      setIsApproveRecalling(true);
      await recallFormResultApproval(
        formResult.id,
        new RecallFormResultApprovalDto({
          reason: result.recallReason,
        }),
      );

      await Promise.all([
        client.invalidateQueries(getFormResultsQueryKey(study!.id)),
        client.invalidateQueries(getPatientRouteProgressQueryKey(patient!.id!)),
        client.invalidateQueries(getPatientStudyCountersQueryKey(study!.id)),
        client.invalidateQueries(getPatientsQueryKey()),
        client.invalidateQueries(getFormResultByIdQueryKey(formResult.id)),
        client.invalidateQueries(getFormHistoryByIdQueryKey(formResult.id)),
      ]);
    } catch (error) {
      showServerErrorToast(error);
    } finally {
      setIsApproveRecalling(false);
    }
  }, [formResult, currentFormResultVersion, recallApproval, client, study, patient]);

  //#region Overview header

  const overviewHeader: ReactElement = useMemo(() => {
    if (!currentFormResultVersion || !formConfig?.type || !formResult || formTab !== FormTab.FormOverview || !patient) {
      return <></>;
    }

    return study?.hasEcrf ? (
      <EcrfOverviewHeader
        patientUniqId={patient.uniqueId}
        currentFormResultVersion={currentFormResultVersion}
        formConfig={formConfig}
        currentFormResult={formResult}
        onEdit={onEditResult}
        stepName={currentStep?.name}
      />
    ) : (
      <OverviewHeader
        patientUniqId={patient.uniqueId}
        currentFormResultVersion={currentFormResultVersion}
        formConfig={formConfig}
        currentFormResult={formResult}
        onEdit={onEditResult}
        stepName={currentStep?.name}
      />
    );
  }, [
    currentFormResultVersion,
    formConfig,
    formResult,
    formTab,
    study?.hasEcrf,
    patient,
    onEditResult,
    currentStep?.name,
  ]);

  const tabs = useMemo(
    () => [
      {
        text: '',
        icon: <MenuIcon />,
        isSelected: formTab === FormTab.FormOverview,
        onClick: () => setFormTab(FormTab.FormOverview),
        testId: 'FormOverview',
      },
      {
        text: '',
        icon: <HistoryIcon />,
        isSelected: formTab === FormTab.HistoryOfChanges,
        onClick: () => setFormTab(FormTab.HistoryOfChanges),
        testId: 'HistoryOfChanges',
      },
    ],
    [formTab, setFormTab],
  );

  const formTopPanel: ReactElement = useMemo(() => {
    if (!currentFormResultVersion || !formConfig?.type || !formResult) {
      return <></>;
    }

    return (
      <div className={Style.overview}>
        <OverviewNavbar items={navbarItems} />
        <Tabs items={tabs} className={Style.icons} tabType="Icon" />
      </div>
    );
  }, [currentFormResultVersion, formConfig?.type, formResult, tabs, navbarItems]);

  //#endregion

  const header = useMemo(() => {
    return (
      <IssueMark
        key={formConfig?.type}
        issueContext={{
          subject: 'Patient',
          topic: 'Records',
          linkedPatientUniqId: patient?.uniqueId,
          topicAdditional: formConfig?.type,
        }}
        position={{ top: 2, right: 8 }}
        countDescendants={false}
        containerClassName={Style.issueMarkContainer}
      >
        <div className={Style.issueMarkTarget} />
      </IssueMark>
    );
  }, [formConfig?.type, patient?.uniqueId]);

  return {
    isLoading: isLoading,
    open: openOverview,
    close: closeOverview,
    element: (
      <>
        {!!patient?.id && formResult && formConfig && (
          <FormModalWithContext
            mode={'Overview'}
            formConfig={formConfig}
            prevAnswersValue={undefined}
            formResultVersion={currentFormResultVersion}
            navigationMenu={
              <div className={Style.navContainer}>
                <IssueMark
                  issueContext={{
                    subject: 'Patient',
                    topic: 'Records',
                    topicAdditional: formConfig?.type,
                    linkedPatientUniqId: patient?.uniqueId,
                    resultId: formResult?.id || undefined,
                    stepName: formConfig?.isMultiInstance ? 'multiple' : currentStep?.name,
                  }}
                  position={{
                    right: -15,
                  }}
                >
                  {formTopPanel}
                </IssueMark>
                {overviewHeader}
              </div>
            }
            isLoading={isLoading}
            footer={
              study?.hasEcrf && (!patient?.archivedBy || !patient?.archivedAt) && formTab === FormTab.FormOverview
                ? recallApproval.component || (
                    <OverviewFooter
                      stepName={currentStep?.name}
                      patientId={patient.id}
                      patientUniqueId={patient?.uniqueId}
                      formType={formConfig.type}
                      formConfigId={formConfig.id}
                      onRecallApproval={recallApprovalHandler}
                      isApproveRecalling={isApproveRecalling}
                      readyForApprove={patient?.monitoringReviewStatus === MonitoringReviewStatusEnum.ReadyForRelease}
                      routeProgress={routeProgress!}
                      formResultVersion={currentFormResultVersion!}
                      onEdit={onEditResult}
                    />
                  )
                : undefined
            }
            formScoreComponent={
              <FormScoreComponent
                formResultVersion={currentFormResultVersion}
                scoreSectionConfig={formConfig.scoreSectionConfig}
              />
            }
            modal={{
              title: formConfig?.type,
              testId: `${formConfig?.type}-overview-form`,
              visible: overviewModal.visible,
              onHide: () => {
                overviewModal.closeModal();
                recallApproval.close();
              },
              header: header,
              headerClassName: Style.modalHeaderMark,
            }}
            patientId={patient.id}
            navigateToNodeId={nodeId}
            stepName={currentStep?.name}
            formTab={formTab}
            setFormTab={setFormTab}
          />
        )}
      </>
    ),
  };
};

const OverviewFooter: FC<{
  formType: string;
  formConfigId: number;
  routeProgress: IRouteProgressDto;
  stepName: string | undefined;
  patientId: string;
  patientUniqueId: string | undefined;
  formResultVersion: IFormResultVersionDto;
  onEdit: (options?: { asSubmitted?: boolean; recallReason?: string }) => void;
  onRecallApproval: () => Promise<void>;
  isApproveRecalling?: boolean;
  readyForApprove?: boolean;
}> = ({
  formResultVersion,
  formConfigId,
  onEdit,
  formType,
  routeProgress,
  stepName,
  patientId,
  patientUniqueId,
  onRecallApproval,
  isApproveRecalling,
  readyForApprove,
}) => {
  const { t } = useScopedTranslation('Forms.Overview');
  const study = useStudy();
  const queryClient = useQueryClient();

  const [isLoading, setIsLoading] = useState(false);

  const recallSubmitHandler = useCallback(async () => {
    try {
      setIsLoading(true);
      await markFormResultAsDraft(formResultVersion.formResultId);
      await Promise.all([
        queryClient.invalidateQueries(getFormResultsQueryKey(study!.id)),
        queryClient.invalidateQueries(getPatientsQueryKey()),
        queryClient.invalidateQueries(getPatientStudyCountersQueryKey(study!.id)),
        queryClient.invalidateQueries(getPatientRouteProgressQueryKey(patientId!)),
        queryClient.invalidateQueries(getFormResultByIdQueryKey(formResultVersion.formResultId)),
        queryClient.invalidateQueries(getFormHistoryByIdQueryKey(formResultVersion.formResultId)),
      ]);
    } catch (error) {
      showServerErrorToast(error);
    } finally {
      setIsLoading(false);
    }
  }, [formResultVersion.formResultId, patientId, queryClient, study]);

  const formResultSkipRevoking = useFormResultSkipRevoking();
  const revokeSkipHandler = useCallback(async () => {
    setIsLoading(true);
    await formResultSkipRevoking.start({
      stepName: stepName,
      routeProgress: routeProgress,
      patientId: patientId,
      formConfigId: formConfigId,
      formResultId: formResultVersion.formResultId,
    });
    setIsLoading(false);
  }, [formConfigId, formResultSkipRevoking, formResultVersion.formResultId, patientId, routeProgress, stepName]);

  const finishingPatientForm = useFinishingPatientFormState(patientId);

  const approveConfirmation = useConfirmationModal();
  const approveHandler = useCallback(async () => {
    const isFinishingPatientForm = finishingPatientForm.type === formType;
    const formContent = isFinishingPatientForm
      ? {
          text: (
            <span>
              <Trans
                i18nKey={'Forms.Overview.ApproveReleasePatientConfirmationModal.Text'}
                values={{ patientUniqueId, formType }}
              >
                <span className={Style.mediumText} />
                <span className={Style.awfulText} />
              </Trans>
            </span>
          ),
          title: t('ApproveReleasePatientConfirmationModal.Title'),
          okButtonText: t('ApproveReleasePatientConfirmationModal.OkButtonText'),
        }
      : {
          text: (
            <span>
              <Trans i18nKey={'Forms.Overview.ApproveConfirmationModal.Text'} values={{ patientUniqueId, formType }}>
                <span className={Style.mediumText} />
                <span className={Style.awfulText} />
              </Trans>
            </span>
          ),
          title: t('ApproveConfirmationModal.Title'),
          okButtonText: t('ApproveConfirmationModal.OkButtonText'),
        };

    const approveResult = await approveConfirmation.open(formContent);
    if (approveResult !== ConfirmResult.Confirm) return;

    try {
      setIsLoading(true);
      await approveFormResult(formResultVersion.formResultId);
      await Promise.all([
        queryClient.invalidateQueries(getFormResultsQueryKey(study!.id)),
        queryClient.invalidateQueries(getPatientRouteProgressQueryKey(patientId)),
        queryClient.invalidateQueries(getPatientStudyCountersQueryKey(study!.id)),
        queryClient.invalidateQueries(getPatientsQueryKey()),
        queryClient.invalidateQueries(getPatientRouteProgressQueryKey(patientId!)),
        queryClient.invalidateQueries(getFormResultByIdQueryKey(formResultVersion.formResultId)),
        queryClient.invalidateQueries(getFormHistoryByIdQueryKey(formResultVersion.formResultId)),
      ]);
    } catch (error: any) {
      showServerErrorToast(error);
    } finally {
      setIsLoading(false);
    }
  }, [
    finishingPatientForm.type,
    formType,
    patientUniqueId,
    t,
    approveConfirmation,
    formResultVersion.formResultId,
    queryClient,
    patientId,
    study,
  ]);

  const { hasStudyClaim } = useHasStudyClaims();
  const isFinalForm = formType === finishingPatientForm?.type;

  const canChangeFormState: { value: boolean; tooltipComponent: ReactNode } = useMemo(() => {
    if (isFinalForm) {
      return {
        value: finishingPatientForm.canBeFilled && !finishingPatientForm.isLoading,
        tooltipComponent: (
          <div className={TypographyStyles.plainText12}>
            <span>{t('Footer.ToEnableApproveNextForms')}</span>
            {finishingPatientForm.notApprovedFormList.map((x, i) => (
              <div key={i} className={Style.tooltipContent}>
                <span>{x.formType}</span>
                <span>{x.stepName ?? x.createdAt}</span>
              </div>
            ))}
          </div>
        ),
      };
    }

    return {
      value:
        (finishingPatientForm?.state === undefined ||
          finishingPatientForm?.state === FormLifecycleState.Draft ||
          finishingPatientForm?.state === FormLifecycleState.ApproveRecalled) &&
        !finishingPatientForm.isLoading,
      tooltipComponent: t('Footer.ToEnableRevokeForm', { formType: finishingPatientForm?.type }),
    };
  }, [isFinalForm, finishingPatientForm, t]);

  const buttonVms = conditionalConcat<AppButtonProps & { key: string; tooltip?: ReactNode }>(
    hasStudyClaim(isFinalForm ? StudyRoleClaimValue.PatientRelease : StudyRoleClaimValue.PatientCreate) &&
      formResultVersion.lifecycleState === FormLifecycleState.Draft && [
        {
          key: 'submit',
          testId: 'submit-button',
          variant: 'button',
          colorSchema: 'primary',
          text: t('Footer.Submit'),
          onClick: () =>
            onEdit({
              asSubmitted: true,
              recallReason: formResultVersion.approvalRecallReason ?? undefined,
            }),
        },
      ],
    hasStudyClaim(isFinalForm ? StudyRoleClaimValue.PatientRelease : StudyRoleClaimValue.PatientCreate) &&
      formResultVersion.lifecycleState === FormLifecycleState.Submitted &&
      !formResultVersion.isSkipped && [
        {
          key: 'RevokeSubmitting',
          testId: 'revoke-submitting-button',
          variant: 'button',
          colorSchema: 'destroy',
          text: t('Footer.RevokeSubmitting'),
          onClick: recallSubmitHandler,
        },
      ],
    hasStudyClaim(isFinalForm ? StudyRoleClaimValue.PatientRelease : StudyRoleClaimValue.PatientCreate) &&
      formResultVersion.lifecycleState === FormLifecycleState.Submitted &&
      formResultVersion.isSkipped && [
        {
          key: 'RevokeSkipping',
          testId: 'revoke-skipping-button',
          variant: 'button',
          colorSchema: 'destroy',
          text: t('Footer.RevokeSkipping'),
          onClick: revokeSkipHandler,
        },
      ],
    hasStudyClaim(isFinalForm ? StudyRoleClaimValue.PatientRelease : StudyRoleClaimValue.PatientCreate) &&
      formResultVersion.lifecycleState === FormLifecycleState.ApproveRecalled && [
        {
          key: 'CreateNewVersion',
          testId: 'create-new-version-button',
          variant: 'button',
          colorSchema: 'primary',
          text: t('Footer.CreateNewVersion'),
          onClick: () =>
            onEdit({
              asSubmitted: false,
              recallReason: formResultVersion.approvalRecallReason ?? undefined,
            }),
        },
      ],
    hasStudyClaim(isFinalForm ? StudyRoleClaimValue.ApproveClosureForm : StudyRoleClaimValue.ApproveForm) &&
      formResultVersion.lifecycleState === FormLifecycleState.Submitted && [
        {
          key: 'Approve',
          testId: 'approve-button',
          variant: 'button',
          colorSchema: 'primary',
          text: t('Footer.Approve'),
          onClick: approveHandler,
          disabled: isFinalForm && !readyForApprove,
          tooltip: isFinalForm && !readyForApprove ? t('Footer.NotReadyForApproveTooltip') : undefined,
          isLoading: isLoading,
        },
      ],
    hasStudyClaim(isFinalForm ? StudyRoleClaimValue.ApproveClosureForm : StudyRoleClaimValue.ApproveForm) &&
      formResultVersion.lifecycleState === FormLifecycleState.Approved && [
        {
          key: 'RevokeApproval',
          testId: 'revoke-approval-button',
          variant: 'button',
          colorSchema: 'destroy',
          text: t('Footer.RevokeApproval'),
          onClick: onRecallApproval,
        },
      ],
  );

  if (buttonVms.length === 0) return null;

  return (
    <div className={Style.footer}>
      <div className={Style.buttonsContainer}>
        {buttonVms.map((x) => (
          // eslint-disable-next-line react/jsx-key
          <Tooltip
            testIdPrefix={'create-new-version'}
            disabled={canChangeFormState.value && !x.tooltip}
            tooltipContent={x.tooltip ?? canChangeFormState.tooltipComponent}
          >
            <AppButton {...x} disabled={!canChangeFormState.value || isLoading || isApproveRecalling || x.disabled} />
          </Tooltip>
        ))}
      </div>
      {formResultSkipRevoking.element}
      {approveConfirmation.modal}
    </div>
  );
};
